import React, { useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { PromptLibraryRequest } from '../../openapi/leo';
import AnalyticsService from '../../services/AnalyticsService';
import {
  chatReply,
  fetchPrompts,
  openLeo,
  saveShowIntro,
} from '../../slices/LeoSlice';
import { AnalyticsEventEnum, AppDispatch, RootState } from '../../types';
import { ContextualPrompt, FollowUp } from '../../utils/LeoUtils';
import NeoLeoPrompt from './Chat/NeoLeoPrompt';
import { PrompSizeVariantType } from './Chat/Prompt';

interface NeoLeoChatPromptsProps {
  promptSuggestions?: ContextualPrompt[];
  isSimba?: boolean;
  promptSizeVariant?: PrompSizeVariantType;
  contextType?: string | null;
  onSelect?(): void;
}

const NeoLeoChatPrompts: React.FC<NeoLeoChatPromptsProps> = ({
  promptSuggestions,
  isSimba = false,
  contextType,
  onSelect,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  const {
    leo: { session, isLeoThinking, currentChat, selectedItems, defaultPrompts },
    auth: { authUserDetail },
  } = useSelector((state: RootState) => ({
    leo: state.leo,
    auth: state.auth,
  }));
  const agentId = authUserDetail?.id;
  const promptReq: PromptLibraryRequest = useMemo(
    () => ({
      source: location.pathname.slice(1),
      limit: 10,
      offset: 0,
    }),
    [location.pathname],
  );

  useEffect(() => {
    if (agentId) {
      dispatch(fetchPrompts(promptReq));
    }
  }, [agentId, dispatch, promptReq]);

  const getFollowUps = (): FollowUp[] | undefined => {
    // for prompts that come from leo chat response
    if (selectedItems.length) {
      return [
        {
          type: contextType || '',
          items: selectedItems,
        },
      ];
    }
    return undefined;
  };

  const onPromptSelect = async (selectedPrompt: ContextualPrompt) => {
    if (onSelect) {
      onSelect();
    }
    dispatch(saveShowIntro(false));
    const updateSessionTitle =
      currentChat.length === 0 ||
      (currentChat.length === 1 &&
        currentChat[0].message === session?.disclaimer);

    await dispatch(
      chatReply(
        session?.id!,
        selectedPrompt.prompt,
        updateSessionTitle,
        selectedPrompt,
        getFollowUps(),
        location.pathname.slice(1),
      ),
    );
    AnalyticsService.instance().logEvent(AnalyticsEventEnum.LEO_PROMPT_CLICK, {
      prompt,
    });
  };

  const prompts = promptSuggestions || defaultPrompts;

  if (isSimba) {
    return (
      <div
        aria-label='leo-simba-prompts'
        className='overflow-x-auto scrollbar-hide flex flex-row justify-start mx-4 py-0.5'
      >
        {prompts.map((prompt: ContextualPrompt) => (
          <NeoLeoPrompt
            key={prompt.prompt}
            prompt={prompt.title}
            onSelect={() =>
              dispatch(openLeo(agentId!, prompt.prompt, prompt, getFollowUps()))
            }
          />
        ))}
      </div>
    );
  }

  return (
    <div className='relative'>
      <div
        className='flex flex-wrap w-full max-h-[136px] overflow-y-auto neo-leo-prompts-scrollbar pb-4 pr-2'
        data-testid='neo-leo-prompts'
      >
        {prompts.map((prompt: ContextualPrompt) => (
          <NeoLeoPrompt
            key={prompt.prompt}
            prompt={prompt.title}
            disabled={!session?.id || isLeoThinking}
            onSelect={() => onPromptSelect(prompt)}
          />
        ))}
      </div>
      <div className='pointer-events-none absolute bottom-0 left-0 w-[98%] h-12 bg-gradient-to-b from-transparent to-reskin-regent-900 z-10' />
    </div>
  );
};

export default NeoLeoChatPrompts;
